import { useState, useEffect } from 'react';
import { useAccount, useReadContract, useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { formatUnits, parseUnits, maxUint256 } from 'viem';
import { CONTRACT_ADDRESSES, MASTER_CHEF_ABI, PAIR_ABI, EMBER_TOKEN_ABI, FTSO_REGISTRY_ABI, ROUTER_ABI } from '../config/contracts';
import { BACKEND_URL } from '../config/api';

function FarmSection({ isConnected, selectedFarm }) {
  const { address } = useAccount();
  const [pools, setPools] = useState([]);
  const [selectedPool, setSelectedPool] = useState(null);
  const [stakeAmount, setStakeAmount] = useState('');
  const [unstakeAmount, setUnstakeAmount] = useState('');
  const [activePanel, setActivePanel] = useState('stake');
  const [status, setStatus] = useState('');
  const [needsApproval, setNeedsApproval] = useState(false);
  const [loadingPools, setLoadingPools] = useState(true);

  // Fetch farm pools from backend
  useEffect(() => {
    const fetchPools = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/farm-pools/list`);
        const data = await response.json();

        setPools(data.pools || []);

        // If a farm was selected from FarmsOverview, use that
        if (selectedFarm) {
          const matchingPool = data.pools.find(p => p.pid === selectedFarm.pid);
          if (matchingPool) {
            setSelectedPool(matchingPool);
          }
        }
        // Otherwise auto-select first pool if available
        else if (data.pools && data.pools.length > 0 && !selectedPool) {
          setSelectedPool(data.pools[0]);
        }
      } catch (error) {
        console.error('Error fetching farm pools:', error);
      } finally {
        setLoadingPools(false);
      }
    };

    fetchPools();
  }, [selectedFarm]);

  const { writeContract, data: txHash, isPending, error: writeError } = useWriteContract();
  const { isLoading: isConfirming, isSuccess, isError: isReceiptError } = useWaitForTransactionReceipt({
    hash: txHash,
    timeout: 120_000, // 2 minutes – prevents UI locking up on slow/unreliable RPC
  });
  const notDeployed = !CONTRACT_ADDRESSES.MASTER_CHEF;

  // Check how many pools exist in MasterChef
  const { data: poolLength } = useReadContract({
    address: CONTRACT_ADDRESSES.MASTER_CHEF,
    abi: MASTER_CHEF_ABI,
    functionName: 'poolLength',
    query: {
      enabled: !!(CONTRACT_ADDRESSES.MASTER_CHEF && !notDeployed),
      retry: false,
      throwOnError: false,
    },
  });

  // Check if the selected pool exists
  const poolExists = poolLength !== undefined && selectedPool?.pid !== undefined && selectedPool?.pid < Number(poolLength);

  const { data: userInfo, error: userInfoError, isError: userInfoIsError, refetch: refetchUserInfo } = useReadContract({
    address: CONTRACT_ADDRESSES.MASTER_CHEF,
    abi: MASTER_CHEF_ABI,
    functionName: 'userInfo',
    args: (address && selectedPool?.pid !== undefined) ? [selectedPool.pid, address] : undefined,
    query: {
      enabled: !!(address && CONTRACT_ADDRESSES.MASTER_CHEF && !notDeployed && poolExists),
      refetchInterval: 30000,
      retry: 3,
      retryDelay: 1000,
      throwOnError: false,
    },
  });

  const { data: pendingReward, error: pendingRewardError, refetch: refetchPendingReward } = useReadContract({
    address: CONTRACT_ADDRESSES.MASTER_CHEF,
    abi: MASTER_CHEF_ABI,
    functionName: 'pendingReward',
    args: (address && selectedPool?.pid !== undefined) ? [selectedPool.pid, address] : undefined,
    query: {
      enabled: !!(address && CONTRACT_ADDRESSES.MASTER_CHEF && !notDeployed && poolExists),
      refetchInterval: 30000,
      retry: 3,
      retryDelay: 1000,
      throwOnError: false,
    },
  });

  const { data: rewardPerBlock } = useReadContract({
    address: CONTRACT_ADDRESSES.MASTER_CHEF,
    abi: MASTER_CHEF_ABI,
    functionName: 'rewardPerBlock',
    query: {
      enabled: !!(CONTRACT_ADDRESSES.MASTER_CHEF && !notDeployed),
      retry: false,
      throwOnError: false,
    },
  });

  const { data: poolInfo } = useReadContract({
    address: CONTRACT_ADDRESSES.MASTER_CHEF,
    abi: MASTER_CHEF_ABI,
    functionName: 'poolInfo',
    args: selectedPool?.pid !== undefined ? [selectedPool.pid] : undefined,
    query: {
      enabled: !!(CONTRACT_ADDRESSES.MASTER_CHEF && !notDeployed && poolExists),
      retry: false,
      throwOnError: false,
    },
  });

  const { data: totalAllocPoint } = useReadContract({
    address: CONTRACT_ADDRESSES.MASTER_CHEF,
    abi: MASTER_CHEF_ABI,
    functionName: 'totalAllocPoint',
    query: {
      enabled: !!(CONTRACT_ADDRESSES.MASTER_CHEF && !notDeployed),
      retry: false,
      throwOnError: false,
    },
  });

  // Get total LP tokens staked in the pool (balance of LP token held by MasterChef)
  const lpTokenAddress = poolInfo ? poolInfo[0] : null;
  const { data: totalStaked } = useReadContract({
    address: lpTokenAddress,
    abi: PAIR_ABI,
    functionName: 'balanceOf',
    args: (lpTokenAddress && CONTRACT_ADDRESSES.MASTER_CHEF) ? [CONTRACT_ADDRESSES.MASTER_CHEF] : undefined,
    query: {
      enabled: !!(lpTokenAddress && CONTRACT_ADDRESSES.MASTER_CHEF && !notDeployed && poolExists),
      refetchInterval: 30000,
      retry: false,
      throwOnError: false,
    },
  });

  // Get user's available (unstaked) LP token balance
  const { data: userLpBalance, error: userLpBalanceError } = useReadContract({
    address: lpTokenAddress,
    abi: PAIR_ABI,
    functionName: 'balanceOf',
    args: (lpTokenAddress && address) ? [address] : undefined,
    query: {
      enabled: !!(lpTokenAddress && address && !notDeployed && poolExists),
      refetchInterval: 30000,
      retry: 3,
      retryDelay: 1000,
      throwOnError: false,
    },
  });

  // Get user's LP token allowance for MasterChef
  const { data: lpAllowance, refetch: refetchAllowance } = useReadContract({
    address: lpTokenAddress,
    abi: PAIR_ABI,
    functionName: 'allowance',
    args: (lpTokenAddress && address && CONTRACT_ADDRESSES.MASTER_CHEF) ? [address, CONTRACT_ADDRESSES.MASTER_CHEF] : undefined,
    query: {
      enabled: !!(lpTokenAddress && address && CONTRACT_ADDRESSES.MASTER_CHEF && !notDeployed && poolExists),
      retry: false,
      throwOnError: false,
    },
  });

  // Get FLR/USD price from FTSO
  const { data: flrPriceData } = useReadContract({
    address: CONTRACT_ADDRESSES.FTSO_REGISTRY,
    abi: FTSO_REGISTRY_ABI,
    functionName: 'getCurrentPriceWithDecimals',
    args: ['FLR'],
    query: {
      enabled: !!CONTRACT_ADDRESSES.FTSO_REGISTRY,
      retry: false,
      throwOnError: false,
    },
  });

  // Get EMBER price in WFLR from Router
  const { data: emberPriceData } = useReadContract({
    address: CONTRACT_ADDRESSES.ROUTER,
    abi: ROUTER_ABI,
    functionName: 'getAmountsOut',
    args: (CONTRACT_ADDRESSES.EMBER_TOKEN && CONTRACT_ADDRESSES.WFLR) ? [
      parseUnits('1', 18), // 1 EMBER
      [CONTRACT_ADDRESSES.EMBER_TOKEN, CONTRACT_ADDRESSES.WFLR]
    ] : undefined,
    query: {
      enabled: !!(CONTRACT_ADDRESSES.ROUTER && !notDeployed && CONTRACT_ADDRESSES.EMBER_TOKEN && CONTRACT_ADDRESSES.WFLR),
      retry: false,
      throwOnError: false,
    },
  });

  // Get LP token reserves
  const { data: reserves } = useReadContract({
    address: lpTokenAddress,
    abi: PAIR_ABI,
    functionName: 'getReserves',
    query: {
      enabled: !!(lpTokenAddress && poolExists),
      retry: false,
      throwOnError: false,
    },
  });

  // Get LP token total supply
  const { data: lpTotalSupply } = useReadContract({
    address: lpTokenAddress,
    abi: PAIR_ABI,
    functionName: 'totalSupply',
    query: {
      enabled: !!(lpTokenAddress && poolExists),
      retry: false,
      throwOnError: false,
    },
  });

  // Handle transaction states
  useEffect(() => {
    if (isPending) {
      setStatus('⏳ Waiting for wallet confirmation...');
    }
  }, [isPending]);

  const EXPLORER = 'https://flare-explorer.flare.network';

  useEffect(() => {
    if (txHash && isConfirming) {
      setStatus(`⏳ Transaction submitted! Waiting for confirmation...\nTx: ${txHash}`);
      console.log('Transaction hash:', txHash);
    }
  }, [txHash, isConfirming]);

  useEffect(() => {
    if (isSuccess) {
      setStatus('✅ Transaction confirmed!');
      setStakeAmount('');
      setUnstakeAmount('');
      // Refresh on-chain data so the UI reflects the new state immediately
      refetchPendingReward();
      refetchUserInfo();
      refetchAllowance();
      console.log('Transaction confirmed successfully!');
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isReceiptError && txHash) {
      setStatus(`⚠️ Could not confirm receipt — check explorer:\n${EXPLORER}/tx/${txHash}`);
    }
  }, [isReceiptError, txHash]);

  useEffect(() => {
    if (writeError) {
      console.error('Write error:', writeError);

      const msg = writeError.message || writeError.shortMessage || '';

      if (msg.includes('User rejected') || msg.includes('user rejected')) {
        setStatus('❌ Transaction rejected by user');
      } else if (msg.includes('insufficient funds')) {
        setStatus('❌ Insufficient FLR for gas fees');
      } else if (msg.includes('Pausable: paused') || msg.includes('paused')) {
        setStatus('❌ Contract is paused — contact the team to unpause');
      } else if (msg.includes('not authorized') || msg.includes('GovernanceToken')) {
        setStatus('❌ EMBER minter not configured — contact the team to fix');
      } else if (msg.includes('execution reverted')) {
        // Strip the raw RPC prefix for a cleaner message
        const reason = msg.replace(/^.*execution reverted:?\s*/i, '') || 'Transaction reverted on-chain';
        setStatus(`❌ ${reason}`);
      } else if (msg) {
        setStatus(`❌ ${msg.slice(0, 120)}`);
      } else {
        setStatus('❌ Transaction failed — check the explorer for details');
      }
    }
  }, [writeError]);

  // Check if LP token needs approval for staking
  useEffect(() => {
    if (lpTokenAddress && stakeAmount && lpAllowance !== undefined) {
      try {
        const needed = parseUnits(stakeAmount, 18);
        setNeedsApproval(lpAllowance < needed);
      } catch {
        setNeedsApproval(false);
      }
    } else {
      setNeedsApproval(false);
    }
  }, [lpTokenAddress, stakeAmount, lpAllowance]);

  // Refetch allowance after successful transaction
  useEffect(() => {
    if (isSuccess && refetchAllowance) {
      refetchAllowance();
    }
  }, [isSuccess, refetchAllowance]);

  const handleStake = () => {
    console.log('=== handleStake called ===');
    console.log('stakeAmount:', stakeAmount);
    console.log('notDeployed:', notDeployed);
    console.log('isConnected:', isConnected);
    console.log('selectedPool:', selectedPool);
    console.log('needsApproval:', needsApproval);
    console.log('lpAllowance:', lpAllowance);

    if (!stakeAmount) {
      setStatus('Please enter an amount to stake');
      console.log('Early return: no stake amount');
      return;
    }
    if (notDeployed) {
      setStatus('Contracts not deployed');
      console.log('Early return: contracts not deployed');
      return;
    }
    if (!isConnected) {
      setStatus('Please connect your wallet');
      console.log('Early return: wallet not connected');
      return;
    }
    if (!selectedPool) {
      setStatus('Please select a pool');
      console.log('Early return: no pool selected');
      return;
    }

    try {
      const amount = parseUnits(stakeAmount, 18);
      console.log('Parsed amount:', amount.toString());
      console.log('Calling writeContract with:', {
        address: CONTRACT_ADDRESSES.MASTER_CHEF,
        functionName: 'deposit',
        args: [selectedPool.pid, amount],
      });

      setStatus('Please confirm the transaction in your wallet...');

      writeContract({
        address: CONTRACT_ADDRESSES.MASTER_CHEF,
        abi: MASTER_CHEF_ABI,
        functionName: 'deposit',
        args: [selectedPool.pid, amount],
      });

      console.log('writeContract called - waiting for user confirmation');
    } catch (e) {
      console.error('Error in handleStake:', e);
      setStatus(`Stake failed: ${e.message || e.shortMessage || 'Unknown error'}`);
    }
  };

  const handleUnstake = async () => {
    if (!unstakeAmount || notDeployed || !isConnected || !selectedPool) return;

    setStatus('⏳ Checking on-chain balance...');

    // Always fetch a fresh on-chain balance immediately before sending.
    // Cached data can be stale if a previous transaction confirmed on-chain
    // but the UI never saw the receipt (old broken-transport issue).
    let stakedOnChain = 0n;
    try {
      const { data: fresh } = await refetchUserInfo();
      stakedOnChain = fresh?.[0] ?? 0n;
    } catch {
      // refetch failed — fall back to cached value if available
      stakedOnChain = userInfo?.[0] ?? 0n;
    }

    if (stakedOnChain === 0n) {
      setStatus('❌ No LP tokens staked in this pool on-chain');
      return;
    }

    let amount;
    try {
      amount = parseUnits(unstakeAmount, 18);
    } catch {
      setStatus('❌ Invalid amount');
      return;
    }

    // Cap at actual on-chain balance to absorb any display rounding
    if (amount > stakedOnChain) {
      amount = stakedOnChain;
    }

    try {
      setStatus('Please confirm the transaction in your wallet...');

      writeContract({
        address: CONTRACT_ADDRESSES.MASTER_CHEF,
        abi: MASTER_CHEF_ABI,
        functionName: 'withdraw',
        args: [selectedPool.pid, amount],
      });

      console.log('Unstake writeContract called - waiting for user confirmation');
    } catch (e) {
      console.error('Error in handleUnstake:', e);
      setStatus(`Unstake failed: ${e.message || e.shortMessage || 'Unknown error'}`);
    }
  };

  const handleHarvest = async () => {
    if (notDeployed || !isConnected || !selectedPool) return;

    // Refresh pending reward so the button label stays accurate
    // and we have a live view of staked amount before the call
    try {
      await refetchPendingReward();
    } catch { /* non-fatal */ }

    try {
      setStatus('Please confirm the transaction in your wallet...');

      writeContract({
        address: CONTRACT_ADDRESSES.MASTER_CHEF,
        abi: MASTER_CHEF_ABI,
        functionName: 'deposit',
        args: [selectedPool.pid, 0n], // deposit 0 = harvest only
      });

      console.log('Harvest writeContract called - waiting for user confirmation');
    } catch (e) {
      console.error('Error in handleHarvest:', e);
      setStatus(`Harvest failed: ${e.message || e.shortMessage || 'Unknown error'}`);
    }
  };

  const handleMaxStake = () => {
    if (userLpBalance) {
      setStakeAmount(formatUnits(userLpBalance, 18));
    }
  };

  const handleMaxUnstake = async () => {
    // Fetch fresh balance so MAX reflects what's actually on-chain right now
    try {
      const { data: fresh } = await refetchUserInfo();
      const bal = fresh?.[0] ?? userInfo?.[0] ?? 0n;
      setUnstakeAmount(formatUnits(bal, 18));
    } catch {
      if (userInfo) setUnstakeAmount(formatUnits(userInfo[0], 18));
    }
  };

  const handleApproveLp = () => {
    if (!lpTokenAddress || notDeployed || !isConnected) return;
    try {
      setStatus('Please confirm the approval in your wallet...');

      writeContract({
        address: lpTokenAddress,
        abi: PAIR_ABI,
        functionName: 'approve',
        args: [CONTRACT_ADDRESSES.MASTER_CHEF, maxUint256],
      });

      console.log('Approval writeContract called - waiting for user confirmation');
    } catch (e) {
      console.error('Error in handleApproveLp:', e);
      setStatus(`Approval failed: ${e.message || e.shortMessage || 'Unknown error'}`);
    }
  };

  const stakedAmount = userInfo ? formatUnits(userInfo[0], 18) : '0.00';

  // Format pending EMBER with enough precision so tiny accruals aren't hidden.
  // Fall back to up-to-6 significant figures for amounts below 0.0001.
  const formatPendingEmber = (raw) => {
    if (!raw) return '0.0000';
    const n = parseFloat(formatUnits(raw, 18));
    if (n === 0) return '0.0000';
    if (n >= 0.0001) return n.toFixed(4);
    // Show up to 6 significant figures for dust amounts
    return n.toPrecision(6).replace(/\.?0+$/, '');
  };
  const pendingEmber = formatPendingEmber(pendingReward);
  const emissionsPerBlock = rewardPerBlock ? parseFloat(formatUnits(rewardPerBlock, 18)).toFixed(2) : '—';
  const availableLpBalance = userLpBalance ? formatUnits(userLpBalance, 18) : '0.00';
  const availableLpBalanceFormatted = parseFloat(availableLpBalance).toFixed(4);

  // Calculate APR with USD pricing
  const calculateAPR = () => {
    if (!rewardPerBlock || !poolInfo || !totalAllocPoint || !totalStaked) return '—';

    try {
      const allocPoint = poolInfo[1];
      const BLOCKS_PER_YEAR = 17520000; // Flare block time ~1.8s

      // Pool's share of emissions
      const poolShare = Number(allocPoint) / Number(totalAllocPoint);

      // Annual EMBER rewards for this pool
      const annualEmberRewards = Number(formatUnits(rewardPerBlock, 18)) * BLOCKS_PER_YEAR * poolShare;

      // Get FLR/USD price
      const flrPriceUSD = flrPriceData && flrPriceData[0] && flrPriceData[2]
        ? Number(formatUnits(flrPriceData[0], Number(flrPriceData[2])))
        : 0;

      // Get EMBER price in WFLR, then convert to USD
      const emberPriceInWFLR = emberPriceData && emberPriceData[1]
        ? Number(formatUnits(emberPriceData[1], 18))
        : 0;
      const emberPriceUSD = emberPriceInWFLR * flrPriceUSD;

      // Calculate LP token price in USD
      let lpTokenPriceUSD = 0;
      if (reserves && lpTotalSupply && flrPriceUSD > 0) {
        const reserve0 = Number(formatUnits(reserves[0], 18));
        const reserve1 = Number(formatUnits(reserves[1], 18));
        const totalSupply = Number(formatUnits(lpTotalSupply, 18));

        // Assuming token1 is always WFLR in our pairs
        const wflrReserveValue = reserve1 * flrPriceUSD;
        // Total liquidity = 2x WFLR side (assuming balanced pool)
        const totalLiquidityUSD = wflrReserveValue * 2;
        lpTokenPriceUSD = totalSupply > 0 ? totalLiquidityUSD / totalSupply : 0;
      }

      // Fallback to simplified calculation if we don't have prices
      if (emberPriceUSD === 0 || lpTokenPriceUSD === 0) {
        const stakedLPAmount = Number(formatUnits(totalStaked, 18));
        if (stakedLPAmount === 0) return '∞%';
        const apr = (annualEmberRewards / stakedLPAmount) * 100;
        if (apr > 999999) return '∞%';
        if (apr > 10000) return `${(apr / 1000).toFixed(0)}K%`;
        if (apr > 1000) return `${(apr / 1000).toFixed(1)}K%`;
        return `${apr.toFixed(2)}%`;
      }

      // USD-denominated APR
      const annualRewardsUSD = annualEmberRewards * emberPriceUSD;
      const stakedLPAmount = Number(formatUnits(totalStaked, 18));
      const totalStakedUSD = stakedLPAmount * lpTokenPriceUSD;

      if (totalStakedUSD === 0) return '∞%';

      const apr = (annualRewardsUSD / totalStakedUSD) * 100;

      if (apr > 999999) return '∞%';
      if (apr > 10000) return `${(apr / 1000).toFixed(0)}K%`;
      if (apr > 1000) return `${(apr / 1000).toFixed(1)}K%`;
      return `${apr.toFixed(2)}%`;
    } catch (e) {
      console.error('APR calculation error:', e);
      return '—';
    }
  };

  const poolAPR = calculateAPR();
  const poolWeight = poolInfo && totalAllocPoint
    ? ((Number(poolInfo[1]) / Number(totalAllocPoint)) * 100).toFixed(1)
    : '—';

  const inputStyle = { width: '100%', padding: '12px', backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '8px', color: '#fff', fontSize: '1rem', boxSizing: 'border-box' };
  const tabStyle = (active) => ({ flex: 1, padding: '10px', backgroundColor: active ? '#FB923C' : '#2a2a2a', color: active ? '#1A0800' : '#fff', border: '1px solid #3a3a3a', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' });

  return (
    <div className="content-section active">
      <div className="section">
        <div className="section-title">Earn $EMBER</div>

        {notDeployed && (
          <div style={{ backgroundColor: '#0f0520', border: '1px solid rgba(109, 40, 217, 0.5)', borderRadius: '8px', padding: '12px', marginBottom: '16px', color: '#C4B5FD', fontSize: '0.875rem', lineHeight: '1.6' }}>
            Contracts not yet deployed. Run: <code>cd contracts && npx hardhat run scripts/deploy.js --network flare</code>
          </div>
        )}

        {!notDeployed && !loadingPools && pools.length === 0 && poolLength !== undefined && Number(poolLength) === 0 && (
          <div style={{ backgroundColor: '#1a0d00', border: '1px solid rgba(251, 146, 60, 0.5)', borderRadius: '8px', padding: '12px', marginBottom: '16px', color: '#FED7AA', fontSize: '0.875rem', lineHeight: '1.6' }}>
            <strong>⚠️ No farming pools configured yet</strong>
            <div style={{ marginTop: '8px' }}>
              To add a farm pool, use the admin panel to create a pool for an approved token.
            </div>
          </div>
        )}

        {!notDeployed && poolLength !== undefined && !poolExists && Number(poolLength) > 0 && selectedPool && (
          <div style={{ backgroundColor: '#1a0d00', border: '1px solid rgba(251, 146, 60, 0.5)', borderRadius: '8px', padding: '12px', marginBottom: '16px', color: '#FED7AA', fontSize: '0.875rem', lineHeight: '1.6' }}>
            <strong>⚠️ Selected pool (pid: {selectedPool.pid}) does not exist</strong>
            <div style={{ marginTop: '8px' }}>
              Available pools: 0 to {Number(poolLength) - 1}
            </div>
          </div>
        )}

        {/* RPC Error Warning */}
        {(userInfoIsError || pendingRewardError || userLpBalanceError) && (
          <div style={{ backgroundColor: '#1a0d00', border: '1px solid rgba(239, 68, 68, 0.5)', borderRadius: '8px', padding: '12px', marginBottom: '16px', color: '#FCA5A5', fontSize: '0.875rem', lineHeight: '1.6' }}>
            <strong>⚠️ RPC Connection Issues Detected</strong>
            <div style={{ marginTop: '8px' }}>
              Having trouble loading your staking data. Your LP tokens are safe, but the blockchain node is experiencing issues.
              <br/><br/>
              <strong>Troubleshooting:</strong>
              <ul style={{ marginTop: '8px', marginLeft: '20px' }}>
                <li>Refresh the page and wait a few moments</li>
                <li>Check your wallet connection</li>
                <li>Your staked tokens are still secure in the contract</li>
              </ul>
            </div>
          </div>
        )}

        {/* Pool selector */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ color: '#9ca3af', fontSize: '0.875rem', display: 'block', marginBottom: '8px' }}>Select Pool</label>
          {loadingPools ? (
            <div style={{ backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '14px 16px', textAlign: 'center', color: '#9ca3af' }}>
              Loading pools...
            </div>
          ) : pools.length === 0 ? (
            <div style={{ backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '14px 16px', textAlign: 'center', color: '#9ca3af' }}>
              No farm pools available yet
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '8px' }}>
              {pools.map(pool => (
                <div
                  key={pool.pid}
                  onClick={() => setSelectedPool(pool)}
                  style={{ backgroundColor: selectedPool?.pid === pool.pid ? '#1a0d35' : '#2a2a2a', border: `1px solid ${selectedPool?.pid === pool.pid ? '#8B5CF6' : '#3a3a3a'}`, borderRadius: '8px', padding: '14px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <div>
                    <div style={{ color: '#fff', fontWeight: '600', marginBottom: '4px' }}>{pool.name}</div>
                    <div style={{ color: '#A78BFA', fontSize: '0.75rem' }}>Earn EMBER</div>
                  </div>
                  {selectedPool?.pid === pool.pid && poolAPR !== '—' && (
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: '#4ade80', fontWeight: '700', fontSize: '1.2rem' }}>
                        {poolAPR}
                      </div>
                      <div style={{ color: '#9ca3af', fontSize: '0.7rem' }}>APR</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
          <div style={{ backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '14px', textAlign: 'center' }}>
            <div style={{ color: '#9ca3af', fontSize: '0.75rem', marginBottom: '4px' }}>APR</div>
            <div style={{ color: '#4ade80', fontWeight: '700', fontSize: '1.2rem' }}>{poolAPR}</div>
          </div>
          <div style={{ backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '14px', textAlign: 'center' }}>
            <div style={{ color: '#9ca3af', fontSize: '0.75rem', marginBottom: '4px' }}>Pool Weight</div>
            <div style={{ color: '#FFB84D', fontWeight: '700' }}>{poolWeight}%</div>
          </div>
          <div style={{ backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '14px', textAlign: 'center' }}>
            <div style={{ color: '#9ca3af', fontSize: '0.75rem', marginBottom: '4px' }}>Your Stake</div>
            <div style={{ color: '#A78BFA', fontWeight: '700' }}>{parseFloat(stakedAmount).toFixed(4)} LP</div>
          </div>
          <div style={{ backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '14px', textAlign: 'center' }}>
            <div style={{ color: '#9ca3af', fontSize: '0.75rem', marginBottom: '4px' }}>Available LP</div>
            <div style={{ color: '#A78BFA', fontWeight: '700' }}>{isConnected ? availableLpBalanceFormatted : '0.0000'} LP</div>
          </div>
          <div style={{ backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '14px', textAlign: 'center' }}>
            <div style={{ color: '#9ca3af', fontSize: '0.75rem', marginBottom: '4px' }}>Pending EMBER</div>
            <div style={{ color: '#4ade80', fontWeight: '700' }}>{pendingEmber}</div>
          </div>
          <div style={{ backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '14px', textAlign: 'center' }}>
            <div style={{ color: '#9ca3af', fontSize: '0.75rem', marginBottom: '4px' }}>Total Staked</div>
            <div style={{ color: '#fff', fontWeight: '700' }}>{totalStaked ? parseFloat(formatUnits(totalStaked, 18)).toFixed(2) : '0.00'} LP</div>
          </div>
          <div style={{ backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '14px', textAlign: 'center' }}>
            <div style={{ color: '#9ca3af', fontSize: '0.75rem', marginBottom: '4px' }}>Emissions / Block</div>
            <div style={{ color: '#fff', fontWeight: '700' }}>{emissionsPerBlock} EMBER</div>
          </div>
        </div>

        {/* Harvest button – use raw BigInt so dust amounts aren't filtered out */}
        {pendingReward !== undefined && pendingReward > 0n && (
          <button
            onClick={handleHarvest}
            disabled={isPending || isConfirming || !selectedPool}
            style={{ width: '100%', marginBottom: '16px', padding: '12px', backgroundColor: '#4ade80', color: '#1a1a1a', border: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '1rem', cursor: 'pointer' }}
          >
            Harvest {pendingEmber} EMBER
          </button>
        )}

        {/* Stake / Unstake tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          <button style={tabStyle(activePanel === 'stake')} onClick={() => setActivePanel('stake')}>Stake LP</button>
          <button style={tabStyle(activePanel === 'unstake')} onClick={() => setActivePanel('unstake')}>Unstake LP</button>
        </div>

        {activePanel === 'stake' && (
          <div style={{ display: 'grid', gap: '12px' }}>
            <div style={{ position: 'relative' }}>
              <input
                style={inputStyle}
                type="number"
                placeholder="Amount of LP tokens to stake"
                value={stakeAmount}
                onChange={e => setStakeAmount(e.target.value)}
              />
              <button
                onClick={handleMaxStake}
                disabled={!isConnected || !userLpBalance || parseFloat(availableLpBalance) === 0}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  padding: '6px 12px',
                  backgroundColor: '#8B5CF6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  cursor: parseFloat(availableLpBalance) > 0 ? 'pointer' : 'not-allowed',
                  opacity: parseFloat(availableLpBalance) > 0 ? 1 : 0.5,
                }}
              >
                MAX
              </button>
            </div>
            {isConnected && (
              <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '-8px', textAlign: 'right' }}>
                Available: {availableLpBalanceFormatted} LP
              </div>
            )}
            {needsApproval && (
              <button
                className="button"
                onClick={handleApproveLp}
                disabled={!isConnected || isPending || isConfirming || notDeployed || !selectedPool}
                style={{ backgroundColor: '#8B5CF6' }}
              >
                {isPending || isConfirming ? 'Approving...' : 'Approve LP Tokens'}
              </button>
            )}
            <button
              className="button"
              onClick={() => {
                console.log('=== Stake button clicked ===');
                console.log('Button disabled state:', {
                  isConnected,
                  stakeAmount,
                  needsApproval,
                  isPending,
                  isConfirming,
                  notDeployed,
                  selectedPool,
                  disabled: !isConnected || !stakeAmount || needsApproval || isPending || isConfirming || notDeployed || !selectedPool
                });
                handleStake();
              }}
              disabled={!isConnected || !stakeAmount || needsApproval || isPending || isConfirming || notDeployed || !selectedPool}
            >
              {isPending || isConfirming ? 'Staking...' : !isConnected ? 'Connect Wallet' : !selectedPool ? 'No Pool Selected' : needsApproval ? 'Approve LP first' : 'Stake LP Tokens'}
            </button>
          </div>
        )}

        {activePanel === 'unstake' && (
          <div style={{ display: 'grid', gap: '12px' }}>
            <div style={{ position: 'relative' }}>
              <input
                style={inputStyle}
                type="number"
                placeholder="Amount of LP tokens to unstake"
                value={unstakeAmount}
                onChange={e => setUnstakeAmount(e.target.value)}
              />
              <button
                onClick={handleMaxUnstake}
                disabled={!isConnected || !userInfo || parseFloat(stakedAmount) === 0}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  padding: '6px 12px',
                  backgroundColor: '#8B5CF6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  cursor: parseFloat(stakedAmount) > 0 ? 'pointer' : 'not-allowed',
                  opacity: parseFloat(stakedAmount) > 0 ? 1 : 0.5,
                }}
              >
                MAX
              </button>
            </div>
            {isConnected && (
              <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '-8px', textAlign: 'right' }}>
                Staked: {parseFloat(stakedAmount).toFixed(4)} LP
              </div>
            )}
            <button
              className="button"
              onClick={handleUnstake}
              disabled={!isConnected || !unstakeAmount || isPending || isConfirming || notDeployed || !selectedPool}
            >
              {isPending || isConfirming ? 'Unstaking...' : !isConnected ? 'Connect Wallet' : !selectedPool ? 'No Pool Selected' : 'Unstake LP Tokens'}
            </button>
          </div>
        )}

        {status && (() => {
          const isOk  = status.includes('✅');
          const isErr = status.includes('❌') || status.includes('⚠️');
          const isPend = status.includes('⏳');
          const [headline, txLine] = status.split('\n');
          const txHash_ = txLine?.replace(/^Tx:\s*/, '').trim();
          const isExplorerLine = txLine?.startsWith(EXPLORER);
          return (
            <div
              style={{
                marginTop: '16px',
                padding: '14px',
                backgroundColor: isOk ? '#0f3a1a' : isErr ? '#3a0f0f' : isPend ? '#1a1a3a' : '#1a1a1a',
                border: `1px solid ${isOk ? '#4ade80' : isErr ? '#ef4444' : isPend ? '#8B5CF6' : '#3a3a3a'}`,
                borderRadius: '8px',
                color: isOk ? '#4ade80' : isErr ? '#ef4444' : isPend ? '#A78BFA' : '#fff',
                fontSize: '0.95rem',
                fontWeight: '600',
                textAlign: 'center',
              }}
            >
              <div>{headline}</div>
              {txLine && (
                isExplorerLine ? (
                  <div style={{ marginTop: '6px', fontSize: '0.78rem', fontWeight: '400' }}>
                    <a href={txLine} target="_blank" rel="noreferrer" style={{ color: '#A78BFA', wordBreak: 'break-all' }}>
                      View on explorer ↗
                    </a>
                  </div>
                ) : txHash_ && (
                  <div style={{ marginTop: '6px', fontSize: '0.78rem', fontWeight: '400' }}>
                    <a
                      href={`${EXPLORER}/tx/${txHash_}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: '#A78BFA', wordBreak: 'break-all' }}
                    >
                      {txHash_.slice(0, 10)}…{txHash_.slice(-8)} ↗
                    </a>
                  </div>
                )
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

export default FarmSection;
